import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Container, Tab, Tabs, ThemeProvider, Typography, createTheme } from "@mui/material";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import DashboardHeaderSection from "../components/common/dashboardheader/DashboardHeader";
import useAccess from "../hooks/useAccess";
import RevenueBudgetReportTab from "../components/payments/reports/RevenueBudgetReportTab";
import DuplicateBankDetailsReportTab from "../components/payments/reports/DuplicateBankDetailsReportTab";
import RevenueTrendReportTab from "../components/payments/reports/RevenueTrendReportTab";

// Same page-shell tokens as components/accessManagement/AccessManagementPage.jsx —
// the other super-admin-only feature screen, and the one DashboardHeaderSection's
// `position: fixed` header + 80px sidebar were already solved for. Reusing them
// (rather than Status.jsx's plain <>...</> shell, which relies on a deeply-nested
// child component to add its own offset) keeps this page from rendering underneath
// the sidebar the way every other page here avoids.
const theme = createTheme({
    typography: { fontFamily: ["Quicksand", "Inter", "sans-serif"].join(",") },
});

const PAGE_BG = {
    minHeight: "100vh",
    background: "linear-gradient(145deg, #EDF1FF 0%, #F2EEFF 38%, #FFF0ED 100%)",
    backgroundAttachment: { xs: "scroll", md: "fixed" },
};

const CONTAINER_SX = {
    // Clears the fixed DashboardHeader (56px below 600px, 64px above) and the
    // fixed 80px sidebar (108px = sidebar + gutter, same value AccessManagementPage uses).
    mt: { xs: "56px", sm: "64px" },
    pt: 3,
    pb: 6,
    pl: { xs: 2, sm: 3, lg: "108px" },
    pr: { xs: 2, sm: 3 },
    overflowX: "hidden",
};

export default function PaymentsReports() {
    const navigate = useNavigate();
    const { isSuperUser } = useAccess();
    const [activeTab, setActiveTab] = useState(0);

    useEffect(() => {
        document.title = "Reports | Meesha";
    }, []);

    // Real enforcement is server-side (both report endpoints 403 a non-super-admin) —
    // this just keeps a non-Super-Admin who lands here from seeing a half-loaded,
    // all-403 page, same guard as AccessManagementPage.jsx.
    useEffect(() => {
        if (!isSuperUser) navigate("/");
    }, [isSuperUser, navigate]);

    if (!isSuperUser) return null;

    return (
        <Box sx={PAGE_BG}>
            <DashboardHeaderSection hideSearch />
            <ThemeProvider theme={theme}>
                <Container maxWidth="xl" sx={CONTAINER_SX}>
                    <Box sx={{ mb: 3.5 }}>
                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.6,
                                bgcolor: "rgba(255,82,0,0.08)",
                                border: "1px solid rgba(255,82,0,0.15)",
                                borderRadius: "20px",
                                px: 1.25,
                                py: 0.35,
                                mb: 1,
                            }}
                        >
                            <ShieldOutlinedIcon sx={{ fontSize: 12, color: "#FF5200" }} />
                            <Typography
                                sx={{
                                    fontFamily: "Quicksand",
                                    fontWeight: 800,
                                    fontSize: 10,
                                    color: "#FF5200",
                                    textTransform: "uppercase",
                                    letterSpacing: 1.2,
                                }}
                            >
                                Admin
                            </Typography>
                        </Box>

                        <Typography
                            sx={{
                                fontFamily: "Quicksand",
                                fontWeight: 900,
                                fontSize: { xs: 28, sm: 34, md: 38 },
                                lineHeight: 1.05,
                                mb: 0.75,
                                background: "linear-gradient(120deg, #1C1C2E 0%, #374151 55%, #FF5200 100%)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text",
                            }}
                        >
                            Reports
                        </Typography>

                        <Typography
                            sx={{
                                fontFamily: "Quicksand",
                                fontWeight: 600,
                                fontSize: { xs: 11, sm: 13 },
                                color: "#9CA3AF",
                            }}
                        >
                            Campaign revenue, budget and payment bank-detail reports — visible to Super Admins only
                        </Typography>
                    </Box>

                    <Tabs
                        value={activeTab}
                        onChange={(e, value) => setActiveTab(value)}
                        variant="scrollable"
                        scrollButtons={false}
                        allowScrollButtonsMobile
                        sx={{
                            minHeight: 0,
                            alignSelf: "flex-start",
                            maxWidth: "100%",
                            display: "inline-flex",
                            bgcolor: "#F3F4F6",
                            borderRadius: "12px",
                            p: 0.5,
                            mb: 3,
                            "& .MuiTabs-indicator": { display: "none" },
                            "& .MuiTabs-flexContainer": { gap: 0.5 },
                        }}
                    >
                        {["Revenue & Budget", "Payment Links & Bank Details", "Revenue Trend"].map((label, index) => {
                            const selected = activeTab === index;
                            return (
                                <Tab
                                    key={label}
                                    label={label}
                                    disableRipple
                                    sx={{
                                        textTransform: "none",
                                        fontFamily: "Quicksand",
                                        fontWeight: 700,
                                        fontSize: 13,
                                        borderRadius: "9px",
                                        minHeight: 36,
                                        px: 2.5,
                                        transition: "all 0.2s ease",
                                        color: selected ? "#fff" : "#6B7280",
                                        background: selected
                                            ? "linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)"
                                            : "transparent",
                                        boxShadow: selected ? "0 2px 8px rgba(255,82,0,0.3)" : "none",
                                        "&.Mui-selected": { color: "#fff" },
                                    }}
                                />
                            );
                        })}
                    </Tabs>

                    {activeTab === 0 && <RevenueBudgetReportTab />}
                    {activeTab === 1 && <DuplicateBankDetailsReportTab />}
                    {activeTab === 2 && <RevenueTrendReportTab />}
                </Container>
            </ThemeProvider>
        </Box>
    );
}
